import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Trophy, CreditCard, Truck, Star, ArrowLeft, MapPin, FileCode, ExternalLink, Clock, RotateCcw, Zap } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { formatApiError, API } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import Countdown from "../components/Countdown";
import OrderChat from "../components/OrderChat";
import BidList from "../components/BidList";
import InvoicePanel from "../components/InvoicePanel";
import RatingReply from "../components/RatingReply";
import RatingDetails from "../components/RatingDetails";
import DisputePanel from "../components/DisputePanel";
import DeliveryProofPanel from "../components/DeliveryProofPanel";
import OrderLifecyclePanel from "../components/OrderLifecyclePanel";
import CardPaymentForm from "../components/CardPaymentForm";
import { BankTransferInstructions, BankTransferStatus } from "../components/BankTransferPanel";
import CreditToggle from "../components/CreditToggle";
import DeliveryInfoForm from "../components/DeliveryInfoForm";
import { Printer, Phone } from "lucide-react";
import InvoiceDataPanel from "../components/InvoiceDataPanel";
import RestaurantSideCard from "../components/RestaurantSideCard";
import PremiumBadge from "../components/PremiumBadge";
import { Award } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STATUS_LABEL = {
  open: "In asta", awarded: "Aggiudicato", paid: "Pagato",
  delivered: "Consegnato", expired: "Scaduto", cancelled: "Annullato", reassigning: "In riassegnazione",
};

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [bids, setBids] = useState([]);
  const [messages, setMessages] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgBody, setMsgBody] = useState("");

  // bid modal
  const [bidOpen, setBidOpen] = useState(false);
  const [bidPrice, setBidPrice] = useState("");
  const [unitPrices, setUnitPrices] = useState([]);
  const [bidHours, setBidHours] = useState(24);
  const [bidNotes, setBidNotes] = useState("");
  const [payMethod, setPayMethod] = useState("card");
  const [payCfg, setPayCfg] = useState({ card_enabled: false, publishable_key: "", paypal_enabled: false });
  const [payOpen, setPayOpen] = useState(false);
  const [paypalCfg, setPaypalCfg] = useState({ enabled: false, client_id: "", currency: "EUR" });
  const [einvCfg, setEinvCfg] = useState({ fic: { enabled: false }, aruba: { enabled: false } });
  const [testingConn, setTestingConn] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [stars, setStars] = useState(5);
  const [aspects, setAspects] = useState({ service: 5, punctuality: 5, quality: 5 });
  const [review, setReview] = useState("");

  // dispute modal
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeFile, setDisputeFile] = useState(null);
  const [disputePreview, setDisputePreview] = useState("");
  const [disputeBusy, setDisputeBusy] = useState(false);
  const [disputePhotoUrl, setDisputePhotoUrl] = useState("");

  const load = useCallback(async () => {
    try {
      const [o, b, r] = await Promise.all([
        api.get(`/orders/${id}`),
        api.get(`/orders/${id}/bids`).catch(() => ({ data: [] })),
        api.get(`/orders/${id}/ratings`).catch(() => ({ data: [] })),
      ]);
      setOrder(o.data);
      setBids(b.data);
      setRatings(r.data);
      // messages only fetched if user is party
      const isRestOwner = user?.role === "restaurant" && o.data.restaurant?.id === user.id;
      const isWinner = user?.role === "supplier" && o.data.winner?.id === user.id;
      const adminMediator = !!user?.is_admin && !!o.data.dispute;
      if (isRestOwner || isWinner || adminMediator) {
        const m = await api.get(`/orders/${id}/messages`).catch(() => ({ data: [] }));
        setMessages(m.data);
      }
    } catch (e) {
      // A supplier loses access when it withdraws, declines or the order is taken from it.
      if (user?.role === "supplier" && e.response?.status === 403) {
        toast.info("Questo ordine non è più disponibile");
        navigate("/fornitore", { replace: true });
        return;
      }
      toast.error(formatApiError(e.response?.data?.detail) || "Impossibile caricare l'ordine");
      navigate(-1);
    } finally { setLoading(false); }
  }, [id, user, navigate]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    api.get("/paypal/config").then(({ data }) => setPaypalCfg(data)).catch(() => {});
    api.get("/payments/config").then(({ data }) => setPayCfg(data)).catch(() => {});
    api.get("/einvoice/config").then(({ data }) => setEinvCfg(data)).catch(() => {});
  }, []);

  const testConnection = async () => {
    setTestingConn(true);
    try {
      const { data } = await api.post("/einvoice/test-connection");
      const parts = [];
      if (data.fic?.ok) parts.push(`✅ FIC: ${data.fic.company_name || "OK"}`);
      else parts.push(`❌ FIC: ${data.fic?.error || "errore"}`);
      if (data.aruba?.ok) parts.push(`✅ Aruba: OK`);
      else parts.push(`❌ Aruba: ${data.aruba?.error || "errore"}`);
      toast(parts.join(" · "));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setTestingConn(false); }
  };

  if (loading || !order) return <div className="mx-auto max-w-4xl px-4 py-10 text-slate-500">Caricamento...</div>;

  const isRestOwner = user?.role === "restaurant" && order.restaurant?.id === user.id;
  const isSupplier = user?.role === "supplier";
  const isWinner = isSupplier && order.winner?.id === user.id;
  const canBid = isSupplier && order.status === "open" && !order.choice_ends_at && !order.outside_working_hours;
  const myBid = bids.find(b => isSupplier && b.supplier?.id === user.id);

  const submitBid = async () => {
    try {
      await api.post(`/orders/${id}/bids`, {
        price: Number(bidPrice), delivery_time_hours: Number(bidHours), notes: bidNotes,
        unit_prices: unitPrices.length ? unitPrices.map(v => (v === "" ? null : Number(v))) : null,
      });
      toast.success("Offerta inviata");
      setBidOpen(false); setBidPrice(""); setBidNotes(""); setUnitPrices([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore invio offerta");
    }
  };

  const selectBid = async (bidId) => {
    try {
      await api.post(`/orders/${id}/select-bid`, { bid_id: bidId });
      toast.success("Offerta selezionata");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const paypalCreate = async () => {
    const { data } = await api.post(`/orders/${id}/paypal/create`);
    return data.paypal_order_id;
  };

  const paypalCapture = async (paypal_order_id) => {
    try {
      await api.post(`/orders/${id}/paypal/capture`, { paypal_order_id });
      toast.success("Pagamento PayPal completato!");
      setPayOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deliver = async () => {
    try {
      await api.post(`/orders/${id}/deliver`, { delivered: true });
      toast.success("Consegna registrata");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rate = async () => {
    try {
      await api.post(`/orders/${id}/rate`, isRestOwner
        ? { review, stars_service: aspects.service, stars_punctuality: aspects.punctuality, stars_quality: aspects.quality }
        : { stars, review });
      toast.success("Recensione inviata");
      setRateOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgBody.trim()) return;
    try {
      const { data } = await api.post(`/orders/${id}/messages`, { body: msgBody });
      setMessages([...messages, data]);
      setMsgBody("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const myRating = ratings.find(r => r.rater_id === user?.id);
  const canRate = order.status === "delivered" && !myRating && (isRestOwner || isWinner);

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Indietro
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="ga-card p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Ordine · {order.category}</div>
                <h1 className="mt-1 font-display text-3xl font-extrabold text-slate-900">{order.title}</h1>
                <div className="mt-2 text-sm text-slate-600 flex items-center gap-1.5 flex-wrap"><MapPin className="w-3.5 h-3.5" /> {order.delivery_address}{order.distance_km != null && <span className="font-mono text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5" data-testid="order-distance-badge">~{order.distance_km} km dalla tua sede</span>}</div>
                {order.delivery_date && <div className="text-sm text-slate-600 mt-1">Consegna richiesta: <span className="font-semibold">{order.delivery_date}</span></div>}
                {order.express_max_price && (
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-2.5 py-1 text-xs font-bold mr-2" data-testid="express-badge">
                    <Zap className="w-3.5 h-3.5" /> Modalità Express · prezzo massimo € {order.express_max_price.toFixed(2)}
                  </div>
                )}
                {order.auction_visibility === "sealed" && order.status === "open" && (
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-800 text-white px-2.5 py-1 text-xs font-bold mr-2" data-testid="sealed-badge">Asta a busta chiusa</div>
                )}
                {order.delivery_time_from && order.delivery_time_to && (
                  <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-900 px-2.5 py-1 text-xs font-semibold" data-testid="delivery-slot-badge">
                    <Clock className="w-3.5 h-3.5" /> Fascia oraria: {order.delivery_time_from} – {order.delivery_time_to}
                  </div>
                )}
                {order.delivery_info && (order.delivery_info.delivery_contact_name || order.delivery_info.delivery_contact_phone || order.delivery_info.unloading_instructions) && (
                  <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="delivery-info-box">
                    <div className="font-mono uppercase tracking-widest text-[10px] text-slate-500 mb-0.5">Consegna e scarico</div>
                    {(order.delivery_info.delivery_contact_name || order.delivery_info.delivery_contact_phone) && <div className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {order.delivery_info.delivery_contact_name}{order.delivery_info.delivery_contact_phone && <> · <a href={`tel:${order.delivery_info.delivery_contact_phone}`} className="font-mono underline">{order.delivery_info.delivery_contact_phone}</a></>}</div>}
                    {order.delivery_info.unloading_instructions && <div className="mt-0.5">{order.delivery_info.unloading_instructions}</div>}
                  </div>
                )}
              </div>
              <div className="shrink-0 flex flex-col items-end gap-2">
                <Badge>{STATUS_LABEL[order.status]}</Badge>
                {(isWinner || isRestOwner) && ["awarded", "paid", "delivered"].includes(order.status) && (
                  <button onClick={() => window.print()} className="no-print inline-flex items-center gap-1 rounded-full border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="print-order-button"><Printer className="w-3.5 h-3.5" /> Stampa</button>
                )}
              </div>
            </div>

            {order.status === "open" && !order.choice_ends_at && (
              <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                <div className="text-xs text-slate-500">Asta chiude tra</div>
                <Countdown endsAt={order.auction_ends_at} testId="order-countdown" />
              </div>
            )}
            {order.status === "open" && order.choice_ends_at && (
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-4" data-testid="choice-window-bar">
                {isRestOwner ? (
                  <>
                    <div className="text-xs text-slate-500">Asta scaduta: scegli un'offerta entro</div>
                    <Countdown endsAt={order.choice_ends_at} testId="choice-countdown" />
                  </>
                ) : (
                  <div className="text-xs text-slate-500">Asta chiusa: in attesa della scelta del ristorante</div>
                )}
              </div>
            )}

            <div className="mt-6">
              <h3 className="font-display font-semibold text-slate-900">Articoli</h3>
              <div className="mt-3 divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
                {order.items.map((it, i) => (
                  <div key={`${it.name}-${i}`} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm bg-white">
                    <div className="col-span-6 sm:col-span-7 font-medium text-slate-900">{it.name}</div>
                    <div className="col-span-3 sm:col-span-2 font-mono text-slate-700">{it.quantity} {it.unit}</div>
                    <div className="col-span-3 text-slate-500 text-xs sm:text-sm truncate">{it.notes}</div>
                  </div>
                ))}
              </div>
            </div>

            {order.notes && (
              <div className="mt-6 p-4 rounded-xl bg-amber-50 border border-amber-100 text-sm text-amber-900">
                <span className="font-semibold">Note: </span>{order.notes}
              </div>
            )}
          </div>

          <OrderLifecyclePanel order={order} isRestOwner={isRestOwner} isWinner={isWinner} onReload={load} onLeave={() => navigate("/fornitore")} />

          {/* Supplier closed on requested delivery day/slot */}
          {isSupplier && order.status === "open" && order.outside_working_hours && (
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="closed-day-banner">
              <Clock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-amber-900">Sei chiuso nel giorno/orario richiesto</div>
                <div className="text-xs text-amber-800 mt-0.5">{order.closed_message}</div>
              </div>
              <Link to="/fornitore" className="shrink-0 rounded-full border border-amber-400 bg-white px-4 py-2 text-xs font-semibold text-amber-900 hover:bg-amber-100" data-testid="closed-day-edit-profile">Aggiorna orari</Link>
            </div>
          )}

          {/* Express-mode accept button (visible to suppliers in category) */}
          {order.express_max_price && order.status === "open" && isSupplier && !order.outside_working_hours && (
            <div className="mt-6 ga-card p-5 border-2 border-emerald-300 bg-emerald-50/40">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-emerald-700">Ordine Express</div>
                  <div className="mt-1 font-display text-2xl font-bold text-slate-900">Accetta a € {order.express_max_price.toFixed(2)}</div>
                  <div className="text-xs text-slate-600 mt-1">Il primo che accetta vince. Nessuna gara al ribasso.</div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/orders/${id}/express-accept`);
                      toast.success("Ordine aggiudicato!");
                      load();
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
                  }}
                  className="ga-btn-primary rounded-full px-6 py-3 font-semibold text-white shrink-0"
                  data-testid="express-accept-button"
                >
                  Accetta ora
                </button>
              </div>
            </div>
          )}

          {/* Bids */}
          <BidList
            bids={bids} order={order} canBid={canBid} myBid={myBid} isRestOwner={isRestOwner}
            bidOpen={bidOpen} setBidOpen={setBidOpen}
            bidPrice={bidPrice} setBidPrice={setBidPrice}
            bidHours={bidHours} setBidHours={setBidHours}
            bidNotes={bidNotes} setBidNotes={setBidNotes}
            unitPrices={unitPrices} setUnitPrices={setUnitPrices}
            onSubmit={submitBid} onSelect={selectBid} isSupplierViewer={isSupplier}
          />

          {/* Chat */}
          {order.winner && (isRestOwner || isWinner || (user?.is_admin && order.dispute)) && (
            <OrderChat
              messages={messages}
              msgBody={msgBody}
              setMsgBody={setMsgBody}
              sendMessage={sendMessage}
              currentUserId={user?.id}
              adminMode={!!user?.is_admin && !isRestOwner && !isWinner}
              disputeOpen={order.confirmation_status === "disputed"}
              orderId={order.id}
              canReport={isRestOwner || isWinner}
              viewerRole={isWinner ? "supplier" : "restaurant"}
            />
          )}

          {/* Invoice data panel (visible after award to both parties) */}
          {order.invoice_data && (
            <InvoiceDataPanel invoice={order.invoice_data} counterpartyRole={isRestOwner ? "supplier" : "restaurant"} />
          )}

          {/* Escrow confirmation UI — acquirente quando ordine è consegnato in hold */}
          {isRestOwner && order.status === "delivered" && order.confirmation_status === "pending" && (
            <div className="ga-card p-5 border-2 border-emerald-300 bg-emerald-50/40" data-testid="escrow-confirm-card">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-emerald-700">Consegna ricevuta · in verifica</div>
                  <div className="mt-1 font-display text-lg font-bold text-slate-900">Conferma o segnala un problema</div>
                  <div className="text-xs text-slate-600 mt-1">
                    Il pagamento è trattenuto. Alla conferma il fornitore riceve il payout. Se non fai nulla entro il termine indicato (1h per Express/Offerte, 6h per ordini in asta), viene auto-confermato.
                  </div>
                  {order.held_until && (
                    <div className="mt-2 text-xs text-slate-500">
                      Auto-conferma il: {new Date(order.held_until).toLocaleString("it-IT")}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={async () => {
                      try { await api.post(`/orders/${id}/confirm-delivery`); toast.success("Consegna confermata · pagamento rilasciato"); load(); }
                      catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
                    }}
                    className="ga-btn-primary rounded-full" data-testid="confirm-delivery-button"
                  >
                    Conferma consegna
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDisputeReason("");
                      setDisputeFile(null);
                      setDisputePreview("");
                      setDisputeOpen(true);
                    }}
                    className="rounded-full border-red-300 text-red-700 hover:bg-red-50" data-testid="dispute-delivery-button"
                  >
                    C'è un problema
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Dispute banner (visible to both) */}
          {order.dispute && (
            <DisputePanel order={order} isWinner={isWinner} isAdmin={!!user?.is_admin} onReload={load} />
          )}
          <DeliveryProofPanel order={order} isWinner={isWinner} onReload={load} />

          {/* Dispute modal */}
          <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle>Segnala un problema con la consegna</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="dispute-reason">Motivo <span className="text-slate-400 text-xs">(min. 10 caratteri)</span></Label>
                  <Textarea
                    id="dispute-reason"
                    rows={4}
                    value={disputeReason}
                    onChange={e => setDisputeReason(e.target.value)}
                    placeholder="Descrivi cosa non è andato bene (merce mancante, qualità, ecc.)"
                    data-testid="dispute-reason-input"
                  />
                  <div className="text-xs text-slate-500 mt-1">{disputeReason.length}/800</div>
                </div>
                <div>
                  <Label htmlFor="dispute-photo">Foto prova <span className="text-slate-400 text-xs">(opzionale · max 5 MB · JPEG/PNG/WebP)</span></Label>
                  <input
                    id="dispute-photo"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) { setDisputeFile(null); setDisputePreview(""); return; }
                      if (f.size > 5 * 1024 * 1024) { toast.error("File troppo grande · massimo 5 MB"); e.target.value = ""; return; }
                      setDisputeFile(f);
                      const reader = new FileReader();
                      reader.onload = ev => setDisputePreview(ev.target.result);
                      reader.readAsDataURL(f);
                    }}
                    className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:rounded-full file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold hover:file:bg-slate-200"
                    data-testid="dispute-photo-input"
                  />
                  {disputePreview && (
                    <img src={disputePreview} alt="Anteprima" className="mt-3 max-h-40 rounded-lg border border-slate-200" data-testid="dispute-photo-preview" />
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-full" onClick={() => setDisputeOpen(false)}>Annulla</Button>
                <Button
                  onClick={async () => {
                    if (disputeReason.trim().length < 10) { toast.error("Motivo troppo breve (min. 10 caratteri)"); return; }
                    setDisputeBusy(true);
                    try {
                      let photoPath = null;
                      if (disputeFile) {
                        const fd = new FormData();
                        fd.append("file", disputeFile);
                        const { data } = await api.post("/uploads/dispute-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        photoPath = data.path;
                      }
                      await api.post(`/orders/${id}/dispute-delivery`, { reason: disputeReason, photo_path: photoPath });
                      toast.success("Contestazione aperta · pagamento congelato");
                      setDisputeOpen(false);
                      load();
                    } catch (e) {
                      toast.error(formatApiError(e.response?.data?.detail) || e.message);
                    } finally {
                      setDisputeBusy(false);
                    }
                  }}
                  disabled={disputeBusy || disputeReason.trim().length < 10}
                  className="ga-btn-primary rounded-full"
                  data-testid="submit-dispute-button"
                >
                  {disputeBusy ? "Invio..." : "Apri contestazione"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Side column */}
        <div className="space-y-6">
          {order.winner && (
            <div className="ga-card p-6">
              <h3 className="font-display text-lg font-bold text-slate-900">Aggiudicazione</h3>
              <div className="mt-3">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Fornitore</div>
                <Link to={`/fornitore/profilo/${order.winner.id}`} className="font-semibold text-slate-900 hover:underline inline-flex items-center gap-1.5 flex-wrap" data-testid="winner-profile-link">
                  {order.winner.name}
                  <ExternalLink className="w-3 h-3 text-slate-400" />
                  {order.winner.is_premium && <PremiumBadge testId="winner-premium-badge" />}
                </Link>
                <div className="text-xs text-slate-500 mt-0.5">★ {order.winner.rating_avg} ({order.winner.rating_count})</div>
              </div>
              {order.winning_bid && (
                <div className="mt-3">
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Importo</div>
                  <div className="font-mono font-bold text-2xl text-emerald-700">€ {order.winning_bid.price.toFixed(2)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Consegna in {order.winning_bid.delivery_time_hours}h</div>
                </div>
              )}
              {order.payment && (
                <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-sm">
                  <div className="text-xs font-mono uppercase tracking-widest text-emerald-700">Pagamento {order.payment.mock ? "· MOCK" : order.payment.method === "card" && !order.payment.captured ? "· Autorizzato" : "· Completato"}</div>
                  <div className="mt-1 font-semibold text-emerald-900" data-testid="payment-method-label">
                    {order.payment.method === "card" ? `Carta ${order.payment.card ? `${order.payment.card.brand.toUpperCase()} •••• ${order.payment.card.last4}` : ""}` : order.payment.method === "bank_transfer" ? "Bonifico bancario" : order.payment.method.toUpperCase()}
                  </div>
                  {order.payment.method === "card" && !order.payment.captured && (
                    <div className="text-xs text-emerald-800 mt-1">Importo bloccato sulla carta · addebito alla conferma consegna{order.payment.capture_status === "capture_failed" ? " · ⚠ cattura fallita" : ""}</div>
                  )}
                  <div className="text-xs font-mono text-emerald-700 mt-1 break-all">{order.payment.reference}</div>
                </div>
              )}
              {order.commission && (
                <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-200 text-sm" data-testid="commission-panel">
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-600 flex items-center gap-1.5">
                    <Award className="w-3 h-3" /> Commissione AstaAlimentare{order.commission.kind === "offer" && <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold" data-testid="commission-offer-badge">Vendi la tua merce · 10% fissa</span>}
                    {order.commission.loyalty_discount_pct > 0 && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 text-indigo-700 px-2 py-0.5 text-[10px] font-bold">
                        -{order.commission.loyalty_discount_pct}% Fedeltà {order.commission.loyalty_stars}★
                      </span>
                    )}
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-slate-700">
                    <div className="flex justify-between"><span>Prezzo</span><span className="font-mono">€ {order.commission.price.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>Fee {order.commission.effective_pct}%</span><span className="font-mono">- € {order.commission.amount.toFixed(2)}</span></div>
                    <div className="flex justify-between font-bold text-slate-900 pt-1 border-t border-slate-200"><span>Netto fornitore</span><span className="font-mono">€ {order.commission.supplier_net.toFixed(2)}</span></div>
                  </div>
                </div>
              )}
              {order.payout && (
                <div className={`mt-3 p-3 rounded-xl border text-sm ${order.payout.status === "failed" ? "bg-red-50 border-red-200" : order.payout.mock ? "bg-slate-50 border-slate-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="payout-panel">
                  <div className={`text-xs font-mono uppercase tracking-widest ${order.payout.status === "failed" ? "text-red-700" : order.payout.mock ? "text-slate-600" : "text-emerald-700"}`}>
                    Accredito fornitore {order.payout.channel === "iban" ? (order.payout.status === "received_directly" ? "· bonifico ricevuto" : "· bonifico su IBAN") : order.payout.mock ? "· off-platform" : "· PayPal"}
                  </div>
                  {order.payout.recipient && <div className="mt-1 text-xs text-slate-600 break-all">{order.payout.recipient}</div>}
                  <div className="mt-1 text-sm font-semibold" data-testid="payout-amount">€ {Number(order.payout.amount || 0).toFixed(2)}</div>
                  {order.payout.commission_amount != null && (
                    <div className="mt-1.5 space-y-0.5 text-[11px] text-slate-600" data-testid="payout-breakdown">
                      <div className="flex justify-between"><span>Pagato dall'acquirente</span><span className="font-mono">€ {Number(order.payout.gross_amount || 0).toFixed(2)}</span></div>
                      <div className="flex justify-between"><span>Commissione AstaAlimentare{order.payout.commission_pct != null ? ` ${order.payout.commission_pct}%` : ""}</span><span className="font-mono">- € {Number(order.payout.commission_amount || 0).toFixed(2)}</span></div>
                      <div className="flex justify-between font-semibold text-slate-800 pt-0.5 border-t border-slate-200"><span>Netto fornitore</span><span className="font-mono">€ {Number(order.payout.net_amount || 0).toFixed(2)}</span></div>
                      {order.payout.commission_billed_separately && <div className="text-amber-700 pt-0.5">Bonifico diretto al fornitore: la commissione viene addebitata al fornitore come "commissione dovuta" (saldo con carta entro 3 giorni).</div>}
                      {order.payout.compensated_amount > 0 && <div className="text-sky-800 pt-0.5" data-testid="payout-compensated">Trattenuti € {Number(order.payout.compensated_amount).toFixed(2)} a saldo di commissioni dovute su altri ordini.</div>}
                    </div>
                  )}
                  {order.payout.batch_id && <div className="text-[10px] font-mono text-slate-500 mt-1 break-all">{order.payout.batch_id}</div>}
                  {order.payout.error && <div className="text-xs text-red-700 mt-1">{order.payout.error}</div>}
                </div>
              )}

              {["awarded", "paid", "delivered"].includes(order.status) && (
                <InvoicePanel
                  order={order} id={id} isWinner={isWinner}
                  einvCfg={einvCfg} testingConn={testingConn}
                  onTestConnection={testConnection} onReload={load}
                />
              )}

              <BankTransferStatus order={order} isWinner={isWinner} onConfirmed={load} />

              {/* Actions */}
              {isRestOwner && order.status === "awarded" && !(order.payment_pending?.method === "bank_transfer") && (
                <Dialog open={payOpen} onOpenChange={setPayOpen}>
                  <DialogTrigger asChild>
                    <Button className="mt-4 w-full ga-btn-primary rounded-full" data-testid="open-pay-modal"><CreditCard className="w-4 h-4 mr-2" />Paga ora</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Pagamento ordine</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Metodo</Label>
                        <Select value={payMethod} onValueChange={setPayMethod}>
                          <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="card" data-testid="pay-method-card">Carta di credito/debito (Visa, Mastercard)</SelectItem>
                            <SelectItem value="bank_transfer" data-testid="pay-method-bank">Bonifico bancario istantaneo</SelectItem>
                            <SelectItem value="paypal" data-testid="pay-method-paypal">PayPal{paypalCfg.enabled ? "" : " (in attivazione)"}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <DeliveryInfoForm order={order} onSaved={setOrder} />
                      <CreditToggle order={order} onChange={(o) => { if (o.status === "paid") { setPayOpen(false); load(); } else setOrder(o); }} />
                      <div className="text-sm">Importo da pagare: <span className="font-mono font-bold" data-testid="pay-amount-due">€ {Math.max(0, (order.winning_bid?.price || 0) - (order.credit_applied || 0)).toFixed(2)}</span></div>
                      {payMethod === "paypal" && !paypalCfg.enabled && (
                        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="paypal-unavailable-notice">
                          <span className="font-bold">PayPal non ancora disponibile</span> · il metodo è in fase di attivazione. Nel frattempo puoi pagare con carta o bonifico istantaneo.
                        </div>
                      )}
                      {payMethod === "card" && (payCfg.card_enabled
                        ? <CardPaymentForm key={order.credit_applied || 0} orderId={id} publishableKey={payCfg.publishable_key} onPaid={() => { setPayOpen(false); load(); }} />
                        : <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">Pagamento con carta non configurato.</div>)}
                      {payMethod === "bank_transfer" && (
                        <BankTransferInstructions orderId={id} amount={(order.winning_bid?.price || 0) - (order.credit_applied || 0)} bank={order.supplier_bank} onDeclared={() => { setPayOpen(false); load(); }} />
                      )}
                      {payMethod === "paypal" && paypalCfg.enabled && (
                        <div className="pt-2">
                          <PayPalScriptProvider options={{ clientId: paypalCfg.client_id, currency: paypalCfg.currency, intent: "capture" }}>
                            <PayPalButtons
                              style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
                              createOrder={paypalCreate}
                              onApprove={(data) => paypalCapture(data.orderID)}
                              onError={(err) => toast.error("PayPal: " + (err?.message || "errore"))}
                            />
                          </PayPalScriptProvider>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {isWinner && order.status === "paid" && order.confirmation_status !== "disputed" && (
                <>
                  <Button onClick={deliver} className="mt-4 w-full ga-btn-primary rounded-full" data-testid="mark-delivered-button">
                    <Truck className="w-4 h-4 mr-2" />Segna come consegnato
                  </Button>
                  <p className="mt-2 text-xs text-slate-600" data-testid="escrow-supplier-hint">
                    Dopo la consegna l'acquirente ha <b>1 ora</b> (Express/Offerte) o <b>6 ore</b> (asta) per confermare o contestare, calcolate dentro la sua fascia di consegna. Se conferma, ricevi subito il payout; altrimenti viene rilasciato automaticamente allo scadere del termine.
                  </p>
                </>
              )}

              {isWinner && order.status === "delivered" && order.confirmation_status === "pending" && (
                <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900" data-testid="escrow-supplier-pending">
                  <div className="font-mono uppercase tracking-widest text-emerald-700 mb-1">Pagamento in attesa di conferma</div>
                  In attesa della conferma dell'acquirente. Se non arriva, il payout viene rilasciato automaticamente
                  {order.held_until ? <> il <b>{new Date(order.held_until).toLocaleString("it-IT")}</b></> : " entro il termine previsto"}.
                </div>
              )}

              {isRestOwner && (order.status === "delivered" || order.status === "expired") && (
                <Link to={`/nuovo-ordine?from=${order.id}`} className="mt-4 w-full inline-flex items-center justify-center rounded-full border-2 border-slate-900 px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-slate-900 hover:text-white transition-colors" data-testid="reorder-button">
                  <RotateCcw className="w-4 h-4 mr-2" />{order.status === "expired" ? "Nessuna offerta: ripubblica l'ordine" : "Riordina con gli stessi articoli"}
                </Link>
              )}

              {canRate && (
                <Dialog open={rateOpen} onOpenChange={setRateOpen}>
                  <DialogTrigger asChild>
                    <Button className="mt-4 w-full rounded-full h-12 text-base font-bold bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-200 ring-2 ring-amber-300 ring-offset-2 animate-pulse hover:animate-none" data-testid="open-rate-modal"><Star className="w-5 h-5 mr-2 fill-white" />Lascia una recensione</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Recensione {isRestOwner ? "fornitore" : "acquirente"}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      {isRestOwner ? (
                        <div className="space-y-3" data-testid="rate-aspects">
                          {[["service", "Servizio"], ["punctuality", "Puntualità"], ["quality", "Qualità"]].map(([k, label]) => (
                            <div key={k} className="flex items-center justify-between gap-3">
                              <Label className="font-mono uppercase tracking-widest text-xs">{label}</Label>
                              <div className="flex gap-1">
                                {[1,2,3,4,5].map(n => (
                                  <button key={n} type="button" onClick={() => setAspects({ ...aspects, [k]: n })} data-testid={`star-${k}-${n}`}>
                                    <Star className={`w-7 h-7 ${n <= aspects[k] ? "fill-amber-500 text-amber-500" : "text-slate-300"}`} />
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                          <div className="text-xs text-slate-500">Media complessiva: <b>{((aspects.service + aspects.punctuality + aspects.quality) / 3).toFixed(1)}</b> ★</div>
                        </div>
                      ) : (
                      <div>
                        <Label>Stelle</Label>
                        <div className="mt-1 flex gap-1">
                          {[1,2,3,4,5].map(n => (
                            <button key={n} type="button" onClick={() => setStars(n)} data-testid={`star-${n}`}>
                              <Star className={`w-7 h-7 ${n <= stars ? "fill-amber-500 text-amber-500" : "text-slate-300"}`} />
                            </button>
                          ))}
                        </div>
                      </div>
                      )}
                      <div>
                        <Label>Commento</Label>
                        <Textarea rows={3} value={review} onChange={e => setReview(e.target.value)} data-testid="rate-review-input" placeholder={isRestOwner ? "Puntualità, qualità, comunicazione..." : "Puntualità pagamenti, comportamento..."} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={rate} className="ga-btn-primary rounded-full" data-testid="submit-rate-button">Invia recensione</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}

          {ratings.length > 0 && (
            <div className="ga-card p-6">
              <h3 className="font-display text-lg font-bold text-slate-900">Recensioni ordine</h3>
              <div className="mt-3 space-y-3">
                {ratings.map(r => (
                  <div key={r.id} className="border border-slate-100 rounded-xl p-3">
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(n => (
                        <Star key={n} className={`w-4 h-4 ${n <= Math.round(r.stars) ? "fill-amber-500 text-amber-500" : "text-slate-300"}`} />
                      ))}
                      <span className="ml-2 text-xs font-mono uppercase tracking-widest text-slate-500">{r.aspect === "service" ? "Servizio" : "Pagamento"}</span>
                    </div>
                    {r.review && <div className="mt-2 text-sm text-slate-700">{r.review}</div>}
                    <RatingDetails details={r.details} />
                    <RatingReply rating={r} supplierName={order.winner?.name} canReply={isWinner && r.target_id === user?.id} onReplied={load} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <RestaurantSideCard restaurant={order.restaurant} />
        </div>
      </div>
    </div>
  );
}
